import {
    derived,
    get,
    type Readable,
    type Writable,
    writable,
} from 'svelte/store';

import { closeRoom as closeRoomByApi, nextRoom as nextRoomByApi } from '@api/room/room';
import type { CloseRoomReason, Room } from '@api/types';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { PlayerStatus } from '@shared/types/room';

import { type Store, type StoreState } from './types';

const initialValues: StoreState = {
    isVideoStopButtonDisabled: true,
    isVideoNextButtonDisabled: true,
    activeRoom: null,
    roomDuration: null,
    isPlayerStarted: false,
    isUserPlayerStarted: false,
};

function createStore(): Store {
    const store = writable<StoreState>(initialValues);
    const { subscribe, update, set } = store;

    const updateStore = (data: Partial<StoreState>) => {
        update((state) => {
            // eslint-disable-next-line no-param-reassign
            state = {
                ...state,
                ...data,
            };
            return state;
        });
    };

    const toggleRoomButtons = (isDisabled: boolean) => {
        updateStore({
            isVideoNextButtonDisabled: isDisabled,
            isVideoStopButtonDisabled: isDisabled,
        });
    };

    const setActiveRoom = (value: Room | null) => {
        updateStore({
            activeRoom: value,
            roomDuration: value?.roomDuration ?? null,
        });
    };

    const setRoomDuration = (roomDuration: number | null) => {
        update((state) => {
            if (roomDuration === null || state.activeRoom) {
                return {
                    ...state,
                    roomDuration: roomDuration ?? null,
                };
            }
            return state;
        });
    };

    const setIsUserPlayerStarted = (value: boolean) => {
        streamingLoggerStore.log(
            { type: LogType.App, message: 'Svelte: Room: Store: setIsUserPlayerStarted' },
            { value },
            { includeExtendedData: true }
        );
        updateStore({
            isUserPlayerStarted: value,
        });
    };

    const setIsPlayerStarted = (value: boolean) => {
        streamingLoggerStore.log(
            { type: LogType.App, message: 'Svelte: Room: Store: setIsPlayerStarted' },
            { value },
            { includeExtendedData: true }
        );
        updateStore({
            isPlayerStarted: value,
        });
    };

    return {
        update,
        set,
        subscribe,
        closeRoom: async (closedReason: CloseRoomReason, closedReasonDetails?: string) => {
            const state = get(store);
            streamingLoggerStore.log(
                { type: LogType.App, message: 'Svelte: Room: Store: closeRoom' },
                { closedReason, closedReasonDetails },
                { includeExtendedData: true }
            );
            toggleRoomButtons(true);
            setActiveRoom(null); // need to prevent confirmation
            if (state.activeRoom) {
                await closeRoomByApi(state.activeRoom.id, { closedReason, closedReasonDetails });
            }
        },
        nextRoom: async (closedReason: CloseRoomReason) => {
            const state = get(store);
            streamingLoggerStore.log(
                {
                    type: LogType.App,
                    message: 'Svelte: Room: Store: nextRoom',
                },
                undefined,
                { exclude: true }
            );
            toggleRoomButtons(true);
            if (state.activeRoom) {
                await nextRoomByApi(state.activeRoom.id, { closedReason });
            }
        },
        onRoomStarted: (room) => {
            streamingLoggerStore.log({ type: LogType.App, message: 'Svelte: Room: Store: onRoomStarted' });
            toggleRoomButtons(false);
            setActiveRoom(room);
        },
        onRoomClosed: () => {
            streamingLoggerStore.log({ type: LogType.App, message: 'Svelte: Room: Store: onRoomClosed' });
            toggleRoomButtons(true);
            setRoomDuration(null);
            setActiveRoom(null);
            setIsUserPlayerStarted(false);
            setIsPlayerStarted(false);
        },
        onRoomSkipped: () => {
            streamingLoggerStore.log({ type: LogType.App, message: 'Svelte: Room: Store: onRoomSkipped' });
        },
        setRoomDuration,
        setIsUserPlayerStarted: setIsUserPlayerStarted,
        setIsPlayerStarted: setIsPlayerStarted,
        reset: () => {
            store.set(initialValues);
        },
    };
}

export const roomStore = createStore();

export const playerStatus: Writable<PlayerStatus | null> = writable(null);

export const roomPaymentStarted: Readable<Room | null> = derived([roomStore], ([$roomStore]) => {
    return $roomStore.activeRoom && $roomStore.isUserPlayerStarted ? $roomStore.activeRoom : null;
});
